import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Spinner } from "components/ui";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { PASSWORD_EXP } from "utils/validation";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetPassword, checkToken } from "actions/auth";
import "./reset-password.scss";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";
import { decodeJwt } from "utils/misc";
import queryString from "query-string";
import { setLanguage } from "reducers/auth";

const ResetPassword = () => {
  const translate = useTranslate();

  useDocumentTitle(translate("resetPassword"));

  // Check for legacy token for backwards compatibility
  const { token } = useParams();

  const location = useLocation();
  const tokenString = queryString.parse(location.search).token || token;
  const decoded = decodeJwt(tokenString);
  const speaksSpanish = decoded?.speaks_spanish;

  const dispatch = useDispatch();
  const [validToken, setIsValidToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  useEffect(() => {
    dispatch(checkToken(tokenString || token)).then(result => {
      setIsValidToken(result);
    });
  }, [tokenString, dispatch]);
  useEffect(() => {
    dispatch(setLanguage(speaksSpanish));
  }, []);

  const getFieldError = (field, errors, touched) =>
    touched[field] ? errors[field] : "";

  // Keep focus in the password field when the eye icon is pressed. Otherwise the
  // field blurs, its error text appears, the centered card shifts and the click
  // on the eye is lost.
  const keepFieldFocus = event => event.preventDefault();

  return (
    <div className="login-screen reset-password" role="main">
      <div className="login-background" aria-hidden="true">
        <div className="white-background"></div>
        <div className="color-background"></div>
      </div>

      <header className="login-screen-header">
        <div
          className="login-logo"
          role="img"
          aria-label="My Sleep Coach Powered by WellSky"
        />
      </header>

      <div className="login-container">
        <div className="login-form-card" role="form">
          <h1 className="login-card-header" id="reset-password-title">
            {translate("resetPassword")}
          </h1>

          {validToken === null && (
            <div
              className="reset-password-status"
              role="status"
              aria-label={translate("checkingLink")}
            >
              <Spinner />
            </div>
          )}

          {validToken === true && (
            <Formik
              initialValues={{
                password: "",
                password_confirmation: "",
                phone_number: ""
              }}
              validationSchema={Yup.object().shape({
                password: Yup.string()
                  .required(translate("required"))
                  .matches(PASSWORD_EXP, translate("passwordRequirements")),
                password_confirmation: Yup.string()
                  .required(translate("required"))
                  .oneOf(
                    [Yup.ref("password")],
                    translate("passwordsDoNotMatch")
                  ),
                phone_number: Yup.string().optional()
              })}
              onSubmit={async values => {
                await dispatch(resetPassword({ ...values, tokenString }));
              }}
            >
              {({
                isSubmitting,
                isValid,
                handleChange,
                handleBlur,
                handleSubmit,
                values,
                errors,
                touched
              }) => (
                <form
                  onSubmit={handleSubmit}
                  aria-labelledby="reset-password-title"
                >
                  <div className="form-field">
                    <TextField
                      id="password"
                      name="password"
                      label={translate("password")}
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!getFieldError("password", errors, touched)}
                      helperText={getFieldError("password", errors, touched)}
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              onClick={() => setShowPassword(show => !show)}
                              onMouseDown={keepFieldFocus}
                              edge="end"
                              type="button"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </div>

                  <div className="form-field">
                    <TextField
                      id="password_confirmation"
                      name="password_confirmation"
                      label={translate("confirmPassword")}
                      value={values.password_confirmation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        !!getFieldError(
                          "password_confirmation",
                          errors,
                          touched
                        )
                      }
                      helperText={getFieldError(
                        "password_confirmation",
                        errors,
                        touched
                      )}
                      type={showPasswordConfirmation ? "text" : "password"}
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPasswordConfirmation
                                  ? "Hide password"
                                  : "Show password"
                              }
                              onClick={() =>
                                setShowPasswordConfirmation(show => !show)
                              }
                              onMouseDown={keepFieldFocus}
                              edge="end"
                              type="button"
                            >
                              {showPasswordConfirmation ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </div>

                  <div className="form-field">
                    <TextField
                      id="phone_number"
                      name="phone_number"
                      type="tel"
                      label={translate("mfaPhoneOptional")}
                      value={values.phone_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="tel"
                      inputProps={{ "aria-describedby": "phone_number-hint" }}
                    />
                    <p
                      className="reset-password-phone-hint"
                      id="phone_number-hint"
                    >
                      {translate("mfaPhoneHint")}
                    </p>
                  </div>

                  {isSubmitting ? (
                    <div
                      className="reset-password-status"
                      role="status"
                      aria-label={translate("resettingPassword")}
                    >
                      <Spinner />
                    </div>
                  ) : (
                    <div className="login-form-actions">
                      <Button
                        variant="contained"
                        color="primary"
                        id="reset-password-button"
                        type="submit"
                        disabled={!isValid}
                      >
                        {translate("resetPassword")}
                      </Button>
                    </div>
                  )}
                </form>
              )}
            </Formik>
          )}

          {validToken === false && (
            <p className="reset-password-invalid" role="alert">
              {translate("sorryLinkNoLongerValid")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
